import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import ShowCard from '../components/ShowCard';
import { Search, Filter, X, Radio } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { key: '', label: 'All' },
  { key: 'cartoon', label: 'Cartoons' },
  { key: 'live_action', label: 'Live Action' },
  { key: 'faith_based', label: 'Faith-Based' },
];

const eras = [
  { key: '', label: 'All Eras' },
  { key: 'early_90s', label: 'Early 90s' },
  { key: 'late_90s', label: 'Late 90s' },
  { key: 'early_2000s', label: 'Early 2000s' },
];

export default function BrowsePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [era, setEra] = useState(searchParams.get('era') || '');
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [yearRange, setYearRange] = useState(null);
  const [timeMachineActive, setTimeMachineActive] = useState(false);

  const timeRanges = [
    { label: '80s Classics', min: 1983, max: 1989, color: '#FF6B00' },
    { label: 'Early 90s', min: 1990, max: 1995, color: '#FF3860' },
    { label: 'Late 90s', min: 1996, max: 1999, color: '#4AC6FF' },
    { label: 'Early 2000s', min: 2000, max: 2006, color: '#FFDE00' },
  ];

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (category) params.category = category;
    if (era && !timeMachineActive) params.era = era;
    if (search) params.search = search;
    if (timeMachineActive && yearRange) {
      params.year_min = yearRange[0];
      params.year_max = yearRange[1];
    }
    axios.get(`${API}/shows`, { params })
      .then(r => { setShows(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [category, era, search, yearRange, timeMachineActive]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const val = e.target.elements.search.value.trim();
    setSearch(val);
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    if (era) params.set('era', era);
    if (val) params.set('search', val);
    setSearchParams(params);
  };

  const clearFilters = () => {
    setCategory('');
    setEra('');
    setSearch('');
    setTimeMachineActive(false);
    setYearRange(null);
    setSearchParams({});
  };

  const hasFilters = category || era || search || timeMachineActive;

  return (
    <div className="page-content">
      <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-['Fredoka'] text-4xl sm:text-5xl font-black text-[#0A0A0A] tracking-tight mb-2" data-testid="browse-title">
            Browse Shows
          </h1>
          <p className="text-base text-[#4B5563] font-['Nunito']">A handpicked collection of classic 90s and early 2000s kids shows, available via YouTube</p>
        </div>

        {/* Filters */}
        <div className="neo-card p-4 mb-8 flex flex-col md:flex-row gap-4 items-start md:items-center" data-testid="filters-section">
          <div className="flex items-center gap-2">
            <Filter size={16} strokeWidth={2.5} className="text-[#4B5563]" />
            <span className="text-sm font-bold text-[#0A0A0A] font-['Fredoka']">Filters:</span>
          </div>
          {/* Category pills */}
          <div className="flex flex-wrap gap-2">
            {categories.map(c => (
              <button
                key={c.key}
                onClick={() => { setCategory(c.key); setSearchParams(p => { if (c.key) p.set('category', c.key); else p.delete('category'); return p; }); }}
                className={`px-3 py-1 text-sm font-bold rounded-lg border-2 border-[#0A0A0A] transition-all ${
                  category === c.key
                    ? 'bg-[#FF3860] text-white shadow-[2px_2px_0px_0px_rgba(10,10,10,1)]'
                    : 'bg-white text-[#0A0A0A] hover:bg-[#F4F4F5]'
                }`}
                data-testid={`filter-category-${c.key || 'all'}`}
              >
                {c.label}
              </button>
            ))}
          </div>
          {/* Era pills */}
          <div className="flex flex-wrap gap-2">
            {eras.map(e => (
              <button
                key={e.key}
                onClick={() => { setEra(e.key); setSearchParams(p => { if (e.key) p.set('era', e.key); else p.delete('era'); return p; }); }}
                className={`px-3 py-1 text-sm font-bold rounded-lg border-2 border-[#0A0A0A] transition-all ${
                  era === e.key
                    ? 'bg-[#4AC6FF] text-[#0A0A0A] shadow-[2px_2px_0px_0px_rgba(10,10,10,1)]'
                    : 'bg-white text-[#0A0A0A] hover:bg-[#F4F4F5]'
                }`}
                data-testid={`filter-era-${e.key || 'all'}`}
              >
                {e.label}
              </button>
            ))}
          </div>
          {/* Search */}
          <form onSubmit={handleSearchSubmit} className="flex-1 min-w-[200px] relative">
            <input
              name="search"
              type="text"
              defaultValue={search}
              placeholder="Search by title..."
              className="w-full pl-8 pr-3 py-2 text-sm border-2 border-[#0A0A0A] rounded-lg bg-white font-['Nunito'] focus:outline-none focus:ring-2 focus:ring-[#FF3860]"
              data-testid="browse-search-input"
            />
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#4B5563]" strokeWidth={2.5} />
          </form>
          {hasFilters && (
            <button onClick={clearFilters} className="flex items-center gap-1 text-sm text-[#FF3860] font-bold hover:underline" data-testid="clear-filters-btn">
              <X size={14} strokeWidth={2.5} /> Clear
            </button>
          )}
        </div>

        {/* Time Machine */}
        <div className="neo-card p-5 mb-8 bg-white" data-testid="time-machine">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Radio size={18} strokeWidth={2.5} className="text-[#FF3860]" />
              <h3 className="font-['Fredoka'] font-bold text-[#0A0A0A]">Time Machine</h3>
              <span className="text-xs text-[#4B5563] font-['Nunito'] hidden sm:inline">Jump to a decade</span>
            </div>
            {timeMachineActive && (
              <button
                onClick={() => { setTimeMachineActive(false); setYearRange(null); }}
                className="text-xs font-bold text-[#FF3860] hover:underline font-['Nunito']"
                data-testid="time-machine-clear"
              >
                Show All
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {timeRanges.map(tr => (
              <button
                key={tr.label}
                onClick={() => {
                  if (timeMachineActive && yearRange && yearRange[0] === tr.min) {
                    setTimeMachineActive(false); setYearRange(null);
                  } else {
                    setTimeMachineActive(true); setYearRange([tr.min, tr.max]); setEra('');
                  }
                }}
                className={`neo-card p-3 text-center transition-all ${
                  timeMachineActive && yearRange && yearRange[0] === tr.min
                    ? 'ring-2 translate-y-[-2px]'
                    : 'bg-white hover:bg-[#F4F4F5]'
                }`}
                style={{
                  borderColor: timeMachineActive && yearRange && yearRange[0] === tr.min ? tr.color : undefined,
                  ringColor: tr.color,
                  backgroundColor: timeMachineActive && yearRange && yearRange[0] === tr.min ? tr.color + '15' : undefined,
                }}
                data-testid={`time-range-${tr.min}-${tr.max}`}
              >
                <span className="font-['Fredoka'] font-bold text-sm text-[#0A0A0A] block">{tr.label}</span>
                <span className="text-xs text-[#4B5563] font-['IBM_Plex_Mono']">{tr.min}–{tr.max}</span>
              </button>
            ))}
          </div>
        </div>

        {/* YouTube Premium Tip */}
        <div className="neo-card p-3 mb-6 bg-[#FDFBEC] flex items-center gap-3" data-testid="yt-premium-tip">
          <span className="text-xs text-[#4B5563] font-['Nunito']">
            <strong className="text-[#0A0A0A] font-['Fredoka']">Ad-free viewing?</strong> If you&apos;re signed into YouTube Premium in your browser, our video player automatically plays without ads. No extra login needed!
          </span>
        </div>

        {/* Results */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="neo-card animate-pulse">
                <div className="aspect-video bg-gray-200" />
                <div className="p-4 space-y-2">
                  <div className="h-5 bg-gray-200 rounded w-3/4" />
                  <div className="h-4 bg-gray-200 rounded w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : shows.length === 0 ? (
          <div className="text-center py-20" data-testid="no-results">
            <p className="font-['Fredoka'] text-2xl text-[#4B5563] mb-2">No shows found</p>
            <p className="text-sm text-[#4B5563] font-['Nunito']">Try adjusting your filters or search</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-[#4B5563] font-['Nunito'] mb-4" data-testid="results-count">
              {shows.length} show{shows.length !== 1 ? 's' : ''} found
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 stagger-children">
              {shows.map((show, i) => (
                <div key={show.id} className="animate-fade-in-up" style={{ opacity: 0 }}>
                  <ShowCard show={show} index={i} />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
